import axios from 'axios';
import { toast } from 'react-toastify';
import envVariables from '../config';

let globalErrorLimit = 1;
let counter = 0;

// Enhanced guest session management
class GuestSessionManager {
  constructor() {
    this.sessionKey = 'guestSessionId';
    this.backupKey = 'guestSessionBackup';
    this.lastUsedKey = 'guestSessionLastUsed';
    this.cartItemsKey = 'guestCartItems';
  }

  // Generate a new guest session ID
  generateSessionId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const userAgent = navigator.userAgent.substring(0, 10);
    const sessionId = `guest_${timestamp}_${random}_${userAgent}`;
    
    // Store backup in multiple places
    this.storeSession(sessionId);
    return sessionId;
  }

  // Store session in multiple locations for persistence
  storeSession(sessionId) {
    try {
      // Primary storage
      localStorage.setItem(this.sessionKey, sessionId);
      
      // Backup storage
      localStorage.setItem(this.backupKey, sessionId);
      
      // Session storage (survives page reloads but not tab closes)
      sessionStorage.setItem(this.sessionKey, sessionId);
      
      // Last used timestamp
      localStorage.setItem(this.lastUsedKey, Date.now().toString());
      
      // Also store in cookies as additional backup
      this.setCookie(this.sessionKey, sessionId, 30); // 30 days
    } catch (error) {
      console.warn('Failed to store guest session:', error);
    }
  }

  // Retrieve existing session from multiple sources
  getExistingSession() {
    try {
      // Try primary localStorage
      let session = localStorage.getItem(this.sessionKey);
      if (session) return session;

      // Try sessionStorage
      session = sessionStorage.getItem(this.sessionKey);
      if (session) {
        this.storeSession(session); // Restore to localStorage
        return session;
      }

      // Try backup localStorage
      session = localStorage.getItem(this.backupKey);
      if (session) {
        this.storeSession(session); // Restore to primary
        return session;
      }

      // Try cookies
      session = this.getCookie(this.sessionKey);
      if (session) {
        this.storeSession(session); // Restore to localStorage
        return session;
      }

      return null;
    } catch (error) {
      console.warn('Failed to retrieve guest session:', error);
      return null;
    }
  }

  // Check if session is still valid (not too old)
  isSessionValid(sessionId) {
    if (!sessionId) return false;
    
    try {
      const lastUsed = localStorage.getItem(this.lastUsedKey);
      if (!lastUsed) return true; // No timestamp, assume valid
      
      const age = Date.now() - parseInt(lastUsed);
      const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days
      
      return age < maxAge;
    } catch (error) {
      return true; // Assume valid if we can't check
    }
  }

  // Attempt to recover session from database
  async recoverSessionFromDatabase() {
    try {
      // Get all potential session IDs from storage
      const sessions = [
        localStorage.getItem(this.sessionKey),
        sessionStorage.getItem(this.sessionKey),
        localStorage.getItem(this.backupKey),
        this.getCookie(this.sessionKey)
      ].filter(Boolean);

      // Try to find a session that has cart items
      for (const sessionId of sessions) {
        if (sessionId && this.isSessionValid(sessionId)) {
          // Test if this session has cart items by making a test API call
          const testResponse = await this.testSessionValidity(sessionId);
          if (testResponse) {
            this.storeSession(sessionId);
            return sessionId;
          }
        }
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to recover session from database:', error);
      return null;
    }
  }

  // Test if a session ID is valid by checking if it has cart items
  async testSessionValidity(sessionId) {
    try {
      // Create a temporary axios instance to test the session
      const testAxios = axios.create({
        headers: {
          'Content-Type': 'application/json',
          'guest-session': sessionId
        }
      });

      // Make a lightweight API call to test session validity
      const response = await testAxios.post(envVariables['REACT_APP_GET_CART'], {
        limit: 1,
        skip: 0,
        getIdFromUserInfo: true
      });

      return response.data && response.data.success;
    } catch (error) {
      return false;
    }
  }

  // Cookie utilities
  setCookie(name, value, days) {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    } catch (error) {
      console.warn('Failed to set cookie:', error);
    }
  }

  getCookie(name) {
    try {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  // Main method to get or create guest session
  async getGuestSession() {
    // First, try to get existing session
    let guestSession = this.getExistingSession();
    
    if (guestSession && this.isSessionValid(guestSession)) {
      // Update last used timestamp
      localStorage.setItem(this.lastUsedKey, Date.now().toString());
      return guestSession;
    }

    // If no valid session exists, try to recover from database
    guestSession = await this.recoverSessionFromDatabase();
    if (guestSession) {
      return guestSession;
    }

    // If all else fails, generate a new session
    guestSession = this.generateSessionId();
  return guestSession;
  }

  // Clear session (used when user logs in or cart is cleared)
  clearSession() {
    try {
      localStorage.removeItem(this.sessionKey);
      localStorage.removeItem(this.backupKey);
      localStorage.removeItem(this.lastUsedKey);
      sessionStorage.removeItem(this.sessionKey);
      this.setCookie(this.sessionKey, '', -1); // Expire cookie
    } catch (error) {
      console.warn('Failed to clear guest session:', error);
    }
  }
}

// Create singleton instance
const guestSessionManager = new GuestSessionManager();

// Function to get or generate a guest session ID (maintained for backward compatibility)
const getGuestSession = async () => {
  return await guestSessionManager.getGuestSession();
};

const apiCall = async (envUrl, type, body = {}, showToast = true) => {
  try {
    if (!type || !envUrl) {
      return false;
    }

    const token = window.localStorage.getItem("token");
    let guestSessionId = null;
    
    if (!token) {
      guestSessionId = await getGuestSession();
    }

    // Set headers based on user type
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      headers["guest-session"] = guestSessionId; // Custom header for guests
    }

    const config = {
      url: envVariables[envUrl],
      method: type,
      headers,
      data: body,
    };

    const { data } = await axios(config);
    if (!data) {
      return false;
    }

    return data;
  } catch (error) {
    try {
      // Handle errors and prevent toast spam
      let { data } = error.response;
      if (data && data.result && counter <= globalErrorLimit) {
        counter++;
        let result = data.result || {};
        if (result.msg === "PARAMETER_ISSUE") {
          let err = result.err || {};
          let list = Object.values(err.list) || [];
          for (let i = 0; i < (list.length > 3 ? 3 : list.length); i++) {
            const element = list[i];
            if (showToast) {
              toast.error(element["message"]);
            }
          }
        } else {
          if (showToast) {
            toast.error(result.msg);
          }
        }

        setTimeout(() => {
          counter = 1;
        }, 4000);
      }
    } catch (error) {
      console.log("apiCall Toast Error ::", error);
    }
    console.log("apiCall Error ::", error);
    return false;
  }
};

// Export both the function and the manager for external use
export { guestSessionManager };
export default apiCall;